/**
 * Enhanced Upload Service - Complete Integration
 * Implements the full upload flow: Azure files → JSON + URLs → TestDino API
 */

import { dirname } from 'path';
import type { Config } from '@/config';
import { parsePlaywrightJson, PlaywrightReport } from '@/core/parser';
import {
  AttachmentScanner,
  AttachmentInfo,
  AttachmentScanResult,
} from '@/core/attachments';
import { GitCollector, GitMetadata } from '@/collectors/git';
import { CiCollector, CiMetadata } from '@/collectors/ci';
import { SystemCollector, SystemMetadata } from '@/collectors/system';
import { ApiClient, ReportUploadResponse } from './api';
import { SasTokenService } from './sas';
import { AzureStorageClient, AzureUploadService } from './azure';
import { createProgressTracker } from '@/utils/progress';
import { createProgressBar } from '@/utils/ui';
import { logger } from '@/utils/logger';
import { withRetry } from '@/utils/retry';
import { NetworkError } from '@/types';

/**
 * Azure upload result for HTML reports and attachment URL mappings
 */
interface AzureUploadResult {
  status: 'uploaded' | 'disabled' | 'failed' | 'not-found';
  url: string;
  urlMapping: Map<string, string>; // Maps original attachment paths to Azure URLs
}

/**
 * Final payload structure matching sample-report.json format
 */
interface FinalPayload {
  config: PlaywrightReport['config'];
  suites: PlaywrightReport['suites'];
  stats: PlaywrightReport['stats'];
  errors: PlaywrightReport['errors'];
  metadata: CompleteMetadata;
}

/**
 * Test configuration metadata extracted from Playwright report
 */
interface TestMetadata {
  framework: {
    name: string;
    version: string;
  };
  config: {
    browsers: Array<{
      browserId: string;
      name: string;
      version: string;
      viewport: string;
      headless: boolean;
      repeatEach: number;
      retries: number;
      testDir: string;
      outputDir: string;
    }>;
    actualWorkers: number;
    timeout: number;
    preserveOutput: string;
    reporters: Array<{ name: string; options: unknown }>;
    grep: unknown;
    grepInvert: unknown;
    fullyParallel: boolean;
    forbidOnly: boolean;
    projects: number;
    shard: unknown;
  };
  customTags: string[];
}

/**
 * Complete metadata structure matching sample-report.json
 */
interface CompleteMetadata {
  git: GitMetadata;
  ci: CiMetadata;
  system: SystemMetadata;
  test: TestMetadata;
  azureUpload?: AzureUploadResult;
}

/**
 * Service to upload Playwright report and metadata to TestDino
 */
export class UploadService {
  private readonly apiClient: ApiClient;

  private readonly sasService: SasTokenService;

  constructor(private readonly config: Config) {
    this.apiClient = new ApiClient(config);
    this.sasService = new SasTokenService(config);
  }

  /**
   * Get report directory from JSON file path
   */
  private getReportDirectory(jsonPath: string): string {
    return dirname(jsonPath);
  }

  /**
   * Generate unique Azure blob path for attachment using UUID
   * This eliminates any possibility of path collisions regardless of directory structure
   */
  private createJsonAttachmentPath(attachment: AttachmentInfo): string {
    // Generate a unique ID for this attachment
    const uniqueId = this.generateUniqueId();

    // Get file extension from original path or content type
    const fileExtension = this.getFileExtension(attachment);

    // Create clean, unique path: json/attachments/{uniqueId}.{extension}
    return `json/attachments/${uniqueId}${fileExtension}`;
  }

  /**
   * Generate unique Azure blob path for trace attachment using UUID
   * Places traces in the same location as other attachments
   */
  private createJsonTraceAttachmentPath(attachment: AttachmentInfo): string {
    // Generate a unique ID for this attachment
    const uniqueId = this.generateUniqueId();

    // Ensure trace files have .zip extension for proper viewer compatibility
    let fileExtension = this.getFileExtension(attachment);
    if (!fileExtension.includes('.zip')) {
      fileExtension = '.zip';
    }

    // Create clean, unique path: json/attachments/traces/{uniqueId}.zip
    return `json/traces/${uniqueId}${fileExtension}`;
  }

  /**
   * Generate a unique identifier for attachments
   * Uses timestamp + random string for uniqueness
   */
  private generateUniqueId(): string {
    const timestamp = Date.now().toString(36); // Base36 timestamp
    const random = Math.random().toString(36).substring(2, 8); // 6 random chars
    return `${timestamp}_${random}`;
  }

  /**
   * Extract file extension from attachment info
   */
  private getFileExtension(attachment: AttachmentInfo): string {
    // First try to get extension from original path
    const pathParts = attachment.originalPath.split('.');
    if (pathParts.length > 1) {
      const extension = pathParts[pathParts.length - 1];
      if (extension) {
        return `.${extension.toLowerCase()}`;
      }
    }

    // Fallback: derive from content type
    const contentType = attachment.contentType.toLowerCase();
    if (contentType.includes('png')) return '.png';
    if (contentType.includes('jpeg') || contentType.includes('jpg'))
      return '.jpg';
    if (contentType.includes('gif')) return '.gif';
    if (contentType.includes('webp')) return '.webp';
    if (contentType.includes('svg')) return '.svg';
    if (contentType.includes('webm')) return '.webm';
    if (contentType.includes('mp4')) return '.mp4';
    if (contentType.includes('zip')) return '.zip';

    // Default fallback
    return '.bin';
  }

  /**
   * Main upload orchestration method
   * Flow: Collect Metadata → Upload Azure Files → Send JSON + URLs to API
   */
  async uploadReport(
    jsonPath: string,
    htmlDir?: string,
    traceDir?: string
  ): Promise<ReportUploadResponse> {
    const tracker = createProgressTracker();

    try {
      // Step 1: Parse the base Playwright report
      tracker.start('Parsing Playwright report...');
      const baseReport = await parsePlaywrightJson(jsonPath);
      tracker.succeed('Report parsed successfully');

      // Step 2: Scan for attachments
      tracker.start('Scanning for attachments...');
      const reportDirectory = this.getReportDirectory(jsonPath);
      const attachmentScanner = new AttachmentScanner(reportDirectory);
      const attachmentScanResult =
        await attachmentScanner.scanAttachments(baseReport);

      // Filter attachments based on configuration
      const attachmentsToUpload = AttachmentScanner.filterAttachments(
        attachmentScanResult,
        this.config
      );

      if (attachmentsToUpload.length > 0) {
        tracker.succeed(
          `Found ${attachmentsToUpload.length} attachments to upload`
        );
      } else {
        tracker.succeed('No attachments to upload based on current flags');
      }

      // Step 3: Collect all metadata
      tracker.start('Collecting environment metadata...');
      const metadata = await this.collectMetadata(baseReport);
      tracker.succeed('Metadata collected');

      // Step 4: Upload files to Azure with STRICT validation after auto-discovery
      let azureUploadResult: AzureUploadResult | undefined;
      const shouldUploadToAzure =
        this.config.uploadImages ||
        this.config.uploadVideos ||
        this.config.uploadHtml ||
        this.config.uploadTraces ||
        this.config.uploadFiles ||
        this.config.uploadFullJson;

      if (shouldUploadToAzure) {
        // SMART VALIDATION: Only fail if report references attachments but files are missing
        this.enforceStrictUploadRequirements(
          htmlDir,
          traceDir,
          attachmentsToUpload,
          attachmentScanResult
        );

        tracker.start('Uploading files to TestDino platform...');
        azureUploadResult = await this.uploadToAzure(
          htmlDir,
          traceDir,
          attachmentsToUpload
        );

        if (azureUploadResult.status === 'uploaded') {
          tracker.succeed('TestDino platform upload completed successfully');
        } else if (azureUploadResult.status === 'failed') {
          tracker.fail('TestDino platform upload failed');
          throw new Error('Upload failed when uploads were explicitly enabled');
        } else {
          tracker.succeed('TestDino platform upload skipped');
        }
      }

      // Step 5: Build final payload with Azure URLs
      tracker.start('Uploading to TestDino API...');

      // Update attachment paths with Azure URLs or status markers
      const urlMapping =
        azureUploadResult?.urlMapping || new Map<string, string>();
      const finalReport = AttachmentScanner.updateAttachmentPaths(
        baseReport,
        urlMapping,
        this.config
      );

      const finalPayload = this.buildFinalPayload(
        finalReport,
        metadata,
        azureUploadResult
      );

      if (this.config.verbose) {
        const testCount = Array.isArray(finalPayload.suites)
          ? finalPayload.suites.length
          : 0;
        logger.verbose(`Uploading report: ${testCount} test suites`);
      }

      // Step 5: Upload to TestDino API with retry
      const response = await withRetry(
        () => this.apiClient.uploadReport(finalPayload),
        { maxAttempts: 3, baseDelay: 1000 }
      );

      tracker.succeed('Report uploaded successfully');
      return response;
    } catch (error) {
      tracker.fail('Upload failed');
      throw error;
    }
  }

  /**
   * Collect all metadata with ZERO data loss guarantee
   * CRITICAL: Each collector has fallbacks - never fails completely
   */
  private async collectMetadata(
    baseReport: PlaywrightReport
  ): Promise<CompleteMetadata> {
    const collectionResults = await Promise.allSettled([
      this.collectGitMetadataWithFallback(),
      this.collectCiMetadataWithFallback(),
      this.collectSystemMetadataWithFallback(),
    ]);

    // Extract results, using fallbacks for any failures
    const gitMeta =
      collectionResults[0].status === 'fulfilled'
        ? collectionResults[0].value
        : this.getGitMetadataFallback();

    const ciMeta =
      collectionResults[1].status === 'fulfilled'
        ? collectionResults[1].value
        : this.getCiMetadataFallback();

    const systemMeta =
      collectionResults[2].status === 'fulfilled'
        ? collectionResults[2].value
        : this.getSystemMetadataFallback();

    // Extract test configuration from the base report
    const testMeta = this.extractTestMetadata(baseReport);

    // Add target environment from config to git metadata
    gitMeta.environment = this.config.environment;

    // Cross-collector reconciliation (e.g. Bitbucket post-merge PR detection)
    CiCollector.reconcileWithGit(ciMeta, gitMeta);

    const metadata = {
      git: gitMeta,
      ci: ciMeta,
      system: systemMeta,
      test: testMeta,
    };

    // Log any collection failures
    const failures = collectionResults
      .map((result, index) => ({
        result,
        type: ['git', 'ci', 'system'][index],
      }))
      .filter(({ result }) => result.status === 'rejected')
      .map(
        ({ type, result }) =>
          `${type}: ${(result as PromiseRejectedResult).reason}`
      );

    if (failures.length > 0) {
      logger.warn(
        `Metadata collection issues (using fallbacks): ${failures.join(', ')}`
      );
    }

    if (this.config.verbose) {
      logger.verbose(
        `Metadata: ${gitMeta.branch || 'unknown branch'}, ${ciMeta.provider || 'local'} environment`
      );
      if (gitMeta.environment && gitMeta.environment !== 'unknown') {
        logger.verbose(`Target Environment: ${gitMeta.environment}`);
      }
    }

    // STRICT ENFORCEMENT: Validate critical metadata when uploads are enabled
    this.enforceStrictMetadataRequirements(metadata);

    return metadata;
  }

  /**
   * Collect Git metadata with comprehensive error handling
   */
  private async collectGitMetadataWithFallback(): Promise<GitMetadata> {
    try {
      return await new GitCollector(process.cwd()).getMetadata();
    } catch (error) {
      if (this.config.verbose) {
        logger.warn(
          `Git metadata collection failed: ${error instanceof Error ? error.message : 'unknown error'}`
        );
      }
      return this.getGitMetadataFallback();
    }
  }

  /**
   * Collect CI metadata with error handling
   */
  private async collectCiMetadataWithFallback(): Promise<CiMetadata> {
    try {
      return CiCollector.collect();
    } catch (error) {
      if (this.config.verbose) {
        logger.warn(
          `CI metadata collection failed: ${error instanceof Error ? error.message : 'unknown error'}`
        );
      }
      return this.getCiMetadataFallback();
    }
  }

  /**
   * Collect System metadata with error handling
   */
  private async collectSystemMetadataWithFallback(): Promise<SystemMetadata> {
    try {
      return SystemCollector.collect();
    } catch (error) {
      if (this.config.verbose) {
        logger.warn(
          `System metadata collection failed: ${error instanceof Error ? error.message : 'unknown error'}`
        );
      }
      return this.getSystemMetadataFallback();
    }
  }

  /**
   * Fallback Git metadata when all collection methods fail
   */
  private getGitMetadataFallback(): GitMetadata {
    const env = process.env;
    return {
      branch:
        env.CI_COMMIT_REF_NAME ||
        env.GITHUB_HEAD_REF ||
        env.BRANCH_NAME ||
        'unknown',
      commit: {
        hash:
          env.CI_COMMIT_SHA || env.GITHUB_SHA || env.GIT_COMMIT || 'unknown',
        message: env.CI_COMMIT_MESSAGE || '',
        author:
          env.CI_COMMIT_AUTHOR || env.GITHUB_ACTOR || env.GIT_AUTHOR_NAME || '',
        email: env.CI_COMMIT_AUTHOR_EMAIL || env.GIT_AUTHOR_EMAIL || '',
        timestamp: new Date().toISOString(),
      },
      repository: {
        name: env.CI_PROJECT_PATH || env.GITHUB_REPOSITORY || 'unknown',
        url:
          env.CI_PROJECT_URL || env.GITHUB_REPOSITORY_URL || env.GIT_URL || '',
      },
      pr: {
        id: env.CI_MERGE_REQUEST_ID || env.GITHUB_PR_NUMBER || '',
        title: env.CI_MERGE_REQUEST_TITLE || '',
        url: env.CI_MERGE_REQUEST_URL || '',
        status: '',
      },
    };
  }

  /**
   * Fallback CI metadata when collection fails
   */
  private getCiMetadataFallback(): CiMetadata {
    const env = process.env;
    return {
      provider: 'unknown',
      pipeline: {
        id: env.CI_PIPELINE_ID || env.GITHUB_RUN_ID || 'unknown',
        name: env.CI_PIPELINE_NAME || env.GITHUB_WORKFLOW || 'CI Pipeline',
        url: env.CI_PIPELINE_URL || '',
      },
      build: {
        number: env.CI_BUILD_NUMBER || env.GITHUB_RUN_NUMBER || 'unknown',
        trigger: env.CI_PIPELINE_TRIGGER || env.GITHUB_EVENT_NAME || '',
      },
      environment: {
        name: env.CI_ENVIRONMENT_NAME || 'local',
        type: '',
        os: 'unknown',
        node: process.version || 'unknown',
      },
    };
  }

  /**
   * Fallback System metadata when collection fails
   */
  private getSystemMetadataFallback(): SystemMetadata {
    return {
      hostname: 'unknown',
      cpu: {
        count: 1,
        model: 'unknown',
      },
      memory: {
        total: 'unknown',
      },
      os: 'unknown',
      nodejs: process.version || 'unknown',
      playwright: 'unknown',
    };
  }

  /**
   * STRICT ENFORCEMENT: Validate we have required files/directories after auto-discovery
   * FAILS HARD if user enabled options but we can't deliver what they requested
   */
  private enforceStrictUploadRequirements(
    htmlDir?: string,
    traceDir?: string,
    attachments: AttachmentInfo[] = [],
    scanResult?: AttachmentScanResult
  ): void {
    const violations: string[] = [];

    // Strict HTML upload validation
    if (this.config.uploadHtml && !htmlDir) {
      violations.push(
        'HTML upload enabled (--upload-html) but no HTML report directory found. ' +
          'Auto-discovery failed - ensure HTML report exists or use --html-report <path>'
      );
    }

    // Smart attachment validation (per-type, unified for all flags):
    // - Flag set + zero entries of that type in report → warn and continue (all tests passed)
    // - Flag set + entries exist but ANY file missing on disk → fail hard
    // Check each enabled type independently so combined flags get per-type errors
    {
      const missingFiles = scanResult?.missingFiles ?? [];

      const typesToCheck: Array<{
        type: string;
        label: string;
        enabled: boolean;
      }> = [
        {
          type: 'image',
          label: 'images',
          enabled: this.config.uploadImages || this.config.uploadFullJson,
        },
        {
          type: 'video',
          label: 'videos',
          enabled: this.config.uploadVideos || this.config.uploadFullJson,
        },
        {
          type: 'file',
          label: 'files',
          enabled: this.config.uploadFiles || this.config.uploadFullJson,
        },
        {
          type: 'trace',
          label: 'traces',
          enabled: this.config.uploadTraces,
        },
      ];

      let anyRelevantFound = false;

      for (const { type, label, enabled } of typesToCheck) {
        if (!enabled) continue;

        const missingOfType = missingFiles.filter(m => m.type === type);
        const foundOfType = attachments.filter(a => a.type === type);
        const totalInReport = foundOfType.length + missingOfType.length;

        if (totalInReport === 0) {
          // No entries of this type in the report — graceful
          continue;
        }

        anyRelevantFound = true;

        if (missingOfType.length > 0) {
          // ANY missing file of this type → fail hard
          const missingPaths = missingOfType.slice(0, 5).map(m => m.path);
          const suffix =
            missingOfType.length > 5
              ? ` (and ${missingOfType.length - 5} more)`
              : '';

          violations.push(
            `${missingOfType.length} ${label} referenced in the report not found on disk:\n` +
              missingPaths.map(p => `  - ${p}`).join('\n') +
              suffix
          );
        }
      }

      if (!anyRelevantFound && typesToCheck.some(t => t.enabled)) {
        logger.warn(
          'Upload flags enabled but no matching attachments found in the report. ' +
            'This is normal when all tests pass without generating attachments.'
        );
      }
    }

    // STRICT FAILURE: If any enabled option cannot be fulfilled, fail hard
    if (violations.length > 0) {
      const errorMessage =
        'STRICT VALIDATION FAILED - Cannot fulfill explicitly enabled upload options:\n\n' +
        violations.join('\n\n') +
        '\n\nEither provide the missing files/directories or remove the corresponding upload flags.';

      throw new Error(errorMessage);
    }

    // Additional validation: Check directory accessibility
    if (htmlDir && this.config.uploadHtml) {
      this.validateDirectoryAccess(htmlDir, 'HTML report directory');
    }

    if (traceDir && this.config.uploadTraces) {
      this.validateDirectoryAccess(traceDir, 'trace directory');
    }
  }

  /**
   * Validate directory is accessible and contains expected content
   */
  private validateDirectoryAccess(dirPath: string, description: string): void {
    try {
      // Basic accessibility check would go here
      // For now, we trust that auto-discovery already validated these
      if (this.config.verbose) {
        logger.success(`${description} validated: ${dirPath}`);
      }
    } catch (error) {
      throw new Error(
        `${description} validation failed: ${dirPath}\n` +
          `${error instanceof Error ? error.message : 'Directory is not accessible'}`,
        { cause: error }
      );
    }
  }

  /**
   * STRICT ENFORCEMENT: Validate critical metadata is available
   * FAILS HARD if essential metadata is missing when uploads are enabled
   */
  private enforceStrictMetadataRequirements(metadata: CompleteMetadata): void {
    const isUploadEnabled =
      this.config.uploadImages ||
      this.config.uploadVideos ||
      this.config.uploadHtml ||
      this.config.uploadTraces ||
      this.config.uploadFiles ||
      this.config.uploadFullJson;

    if (!isUploadEnabled) {
      return; // No strict requirements for JSON-only uploads
    }

    const violations: string[] = [];

    // Strict Git metadata validation
    if (!metadata.git.commit?.hash || metadata.git.commit.hash === 'unknown') {
      violations.push(
        'Git commit hash missing or unknown. ' +
          "Required for upload tracking - ensure you're in a git repository with commits"
      );
    }

    if (!metadata.git.branch || metadata.git.branch === 'unknown') {
      violations.push(
        'Git branch information missing or unknown. ' +
          "Required for upload organization - ensure you're on a valid git branch"
      );
    }

    // Strict repository metadata validation
    if (
      !metadata.git.repository?.name ||
      metadata.git.repository.name === 'unknown'
    ) {
      violations.push(
        'Repository name missing or unknown. ' +
          'Required for upload categorization - ensure git remote is configured'
      );
    }

    // STRICT FAILURE: If critical metadata is missing during uploads, fail hard
    if (violations.length > 0) {
      const errorMessage =
        'STRICT METADATA VALIDATION FAILED - Missing critical information for uploads:\n\n' +
        violations.join('\n\n') +
        '\n\nEither fix the git repository setup or disable upload options for JSON-only uploads.';

      throw new Error(errorMessage);
    }

    if (this.config.verbose) {
      logger.success(
        'Strict metadata validation passed - all critical information available'
      );
    }
  }

  /**
   * Validate and sanitize path prefix from SAS response
   * CRITICAL: Ensures reliable URL generation even with malformed server responses
   */
  private validateAndSanitizePathPrefix(sasResponse: unknown): string {
    // New format uses blobPath at top level
    const pathPrefix = (sasResponse as Record<string, unknown>)?.blobPath;

    if (!pathPrefix || typeof pathPrefix !== 'string') {
      // Generate fallback path prefix based on current timestamp
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const randomId = Math.random().toString(36).substring(2, 10);

      const fallbackPrefix = `${year}/${month}/${day}/${randomId}`;

      if (this.config.verbose) {
        logger.warn(
          `Invalid or missing blobPath from server, using fallback: ${fallbackPrefix}`
        );
      }

      return fallbackPrefix;
    }

    // Sanitize path prefix to ensure it's safe
    const sanitized = pathPrefix
      .replace(/^\/+|\/+$/g, '') // Remove leading/trailing slashes
      .replace(/\/+/g, '/') // Replace multiple slashes with single slash
      .replace(/[^a-zA-Z0-9/\-_]/g, ''); // Remove unsafe characters

    if (!sanitized) {
      // If sanitization resulted in empty string, use fallback
      const now = new Date();
      const fallbackPrefix = `${now.getFullYear()}/${String(now.getMonth() + 1).padStart(2, '0')}/${String(now.getDate()).padStart(2, '0')}/fallback_${Date.now()}`;

      if (this.config.verbose) {
        logger.warn(
          `Path prefix sanitization resulted in empty string, using fallback: ${fallbackPrefix}`
        );
      }

      return fallbackPrefix;
    }

    if (sanitized !== pathPrefix && this.config.verbose) {
      logger.warn(`Path prefix sanitized: '${pathPrefix}' → '${sanitized}'`);
    }

    return sanitized;
  }

  /**
   * Validate SAS response completeness and generate safe fallbacks
   */
  private validateSasResponse(sasResponse: unknown): void {
    // New format: sasToken, containerUrl, blobPath, maxSize, allowedFileTypes at top level
    const required = ['sasToken', 'containerUrl', 'blobPath'];

    const missing = [];
    const response = sasResponse as Record<string, unknown>;

    for (const field of required) {
      if (!(field in response) || !response[field]) {
        missing.push(field);
      }
    }

    if (missing.length > 0) {
      throw new Error(
        `Invalid SAS response from server - missing required fields: ${missing.join(', ')}\n` +
          'This indicates a server-side issue. Please contact support.'
      );
    }

    // Validate data types and ranges
    if (typeof response.maxSize !== 'number' || response.maxSize <= 0) {
      throw new Error('Invalid maxSize in SAS response');
    }

    if (!Array.isArray(response.allowedFileTypes)) {
      throw new Error('Invalid allowedFileTypes in SAS response');
    }
  }

  /**
   * Extract test configuration metadata from Playwright report
   */
  private extractTestMetadata(report: PlaywrightReport): TestMetadata {
    const config = report.config || {};
    const projects = config.projects || [];

    // Get Playwright version
    let playwrightVersion = 'unknown';
    try {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      const pkg = require('playwright/package.json');
      playwrightVersion = pkg.version || 'unknown';
    } catch {
      try {
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        const pkg = require('@playwright/test/package.json');
        playwrightVersion = pkg.version || 'unknown';
      } catch {
        // Keep default 'unknown'
      }
    }

    // Build browser configurations
    const browsers = projects.map(
      (project: PlaywrightReport['config']['projects'][number]) => ({
        browserId: project.id || project.name || 'unknown',
        name: project.name || 'unknown',
        version: config.version || 'unknown',
        viewport: '1280x720', // Default, could be extracted from project config
        headless: true, // Default assumption
        repeatEach: project.repeatEach || 1,
        retries: project.retries || 0,
        testDir: project.testDir || config.rootDir || 'unknown',
        outputDir: project.outputDir || 'unknown',
      })
    );

    return {
      framework: {
        name: 'playwright',
        version: playwrightVersion,
      },
      config: {
        browsers,
        actualWorkers: config.metadata?.actualWorkers || config.workers || 1,
        timeout: projects[0]?.timeout || config.timeout || 30000,
        preserveOutput: config.preserveOutput || 'always',
        reporters: this.extractReporterConfig(config.reporter),
        grep: config.grep || {},
        grepInvert: config.grepInvert || null,
        fullyParallel: config.fullyParallel || false,
        forbidOnly: config.forbidOnly || false,
        projects: projects.length || 0,
        shard: config.shard || null,
      },
      customTags: this.config.tags || [],
    };
  }

  /**
   * Extract reporter configuration from Playwright config
   */
  private extractReporterConfig(
    reporters: PlaywrightReport['config']['reporter']
  ): PlaywrightReport['config']['reporter'] {
    if (!Array.isArray(reporters)) return [];

    return reporters.map(reporter => {
      if (Array.isArray(reporter)) {
        return {
          name: reporter[0],
          options: reporter[1] || {},
        };
      }
      return {
        name: reporter,
        options: {},
      };
    });
  }

  /**
   * Upload HTML, trace files, and attachments to Azure storage with proper directory structure
   */
  private async uploadToAzure(
    htmlDir?: string,
    traceDir?: string,
    attachments: AttachmentInfo[] = []
  ): Promise<AzureUploadResult> {
    try {
      // Request SAS token with retry (ONE TOKEN PER COMMAND)
      const sasResponse = await withRetry(
        () => this.sasService.requestSasToken(),
        { maxAttempts: 3, baseDelay: 1000 }
      );

      // Validate SAS response completeness
      this.validateSasResponse(sasResponse);

      // Validate and sanitize path prefix
      const sanitizedPathPrefix =
        this.validateAndSanitizePathPrefix(sasResponse);

      if (this.config.verbose) {
        const expiryMinutes = Math.floor(
          (new Date(sasResponse.expiresAt).getTime() - Date.now()) / 60000
        );
        logger.verbose(
          `Storage token acquired (expires in ${expiryMinutes} min)`
        );
        logger.verbose(`Upload path: ${sanitizedPathPrefix}/`);
      }

      // Create Azure storage client with validated path prefix
      const validatedSasResponse = {
        ...sasResponse,
        blobPath: sanitizedPathPrefix,
      };

      const storageClient = new AzureStorageClient(validatedSasResponse);
      const uploadService = new AzureUploadService(storageClient);

      let htmlUrl = '';
      const urlMapping = new Map<string, string>();

      // Upload attachments (images, videos, etc.) and collect URL mappings
      if (attachments.length > 0) {
        try {
          logger.info(`Uploading ${attachments.length} attachments...`);

          // Create progress bar for attachment uploads
          const progressBar = createProgressBar();
          progressBar.start(attachments.length, 0);

          // Upload attachments in configurable batches for better performance
          const batchSize = this.config.batchSize;
          let uploadedCount = 0;
          let failedCount = 0;

          for (let i = 0; i < attachments.length; i += batchSize) {
            const batch = attachments.slice(i, i + batchSize);

            // Upload batch in parallel and collect results
            const uploadPromises = batch.map(async attachment => {
              try {
                // Create clean blob path for JSON attachments with directory structure
                const cleanPath = this.createJsonAttachmentPath(attachment);
                const uploadedUrl = await storageClient.uploadFile(
                  attachment.absolutePath,
                  cleanPath
                );

                const fileName =
                  attachment.absolutePath.split('/').pop() || attachment.name;
                progressBar.increment({ filename: fileName });

                return { attachment, uploadedUrl };
              } catch (error) {
                failedCount++;
                const fileName =
                  attachment.absolutePath.split('/').pop() || attachment.name;
                progressBar.increment({ filename: `(failed) ${fileName}` });
                if (this.config.verbose) {
                  logger.warn(
                    `${attachment.name}: ${error instanceof Error ? error.message : 'Upload failed'}`
                  );
                }
                return null;
              }
            });

            const results = await Promise.all(uploadPromises);

            // Collect successful uploads into URL mapping
            for (const result of results) {
              if (result?.uploadedUrl) {
                uploadedCount++;
                urlMapping.set(
                  result.attachment.originalPath,
                  result.uploadedUrl
                );
              }
            }
          }

          progressBar.stop();

          if (failedCount > 0) {
            logger.warn(
              `Uploaded ${uploadedCount}/${attachments.length} attachments (${failedCount} failed)`
            );
          } else {
            logger.success(`Uploaded ${uploadedCount} attachments`);
          }
        } catch (error) {
          logger.warn(
            `Attachment upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`
          );
        }
      }

      // Upload HTML report if enabled and directory exists
      const shouldUploadHtmlDir = this.config.uploadHtml && htmlDir;
      if (shouldUploadHtmlDir) {
        try {
          if (this.config.verbose) {
            logger.verbose('Uploading HTML report...');
          }

          // Upload directory contents with filtering based on flags
          const htmlConfig = {
            uploadImages: this.config.uploadImages || this.config.uploadHtml,
            uploadVideos: this.config.uploadVideos || this.config.uploadHtml,
            uploadHtml: this.config.uploadHtml,
          };
          const uploadedUrls =
            await uploadService.uploadHtmlDirectoryWithProgress(
              htmlDir,
              'html',
              htmlConfig
            );

          // Build HTML URL - find the index.html in uploaded URLs
          const indexUrl = uploadedUrls.find(url => url.endsWith('index.html'));
          if (indexUrl) {
            htmlUrl = indexUrl;
          } else {
            // Fallback: construct URL manually using validated path prefix
            htmlUrl = `${sasResponse.containerUrl}/${sanitizedPathPrefix}/html/index.html`;
          }

          logger.success('HTML report uploaded');
        } catch (error) {
          logger.error(
            `HTML upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`
          );
          // htmlUrl remains empty, which will result in 'failed' status
        }
      }

      // Upload trace files - try JSON attachments first, then fallback to trace directory
      if (this.config.uploadTraces) {
        const traceAttachments = attachments.filter(
          attachment => attachment.type === 'trace'
        );

        if (traceAttachments.length > 0) {
          // Upload individual trace files from JSON attachments
          try {
            logger.info(`Uploading ${traceAttachments.length} trace files...`);

            let uploadedTraces = 0;
            for (const traceAttachment of traceAttachments) {
              try {
                // Create clean blob path for trace files with proper naming and .zip extension
                const cleanPath =
                  this.createJsonTraceAttachmentPath(traceAttachment);
                const uploadedUrl = await storageClient.uploadFile(
                  traceAttachment.absolutePath,
                  cleanPath
                );

                if (this.config.verbose) {
                  const fileName = traceAttachment.absolutePath
                    .split('/')
                    .pop();
                  logger.success(`${fileName}`);
                }

                // Add to URL mapping so it gets updated in the JSON
                urlMapping.set(traceAttachment.originalPath, uploadedUrl);
                uploadedTraces++;
              } catch (error) {
                if (this.config.verbose) {
                  logger.warn(
                    `${traceAttachment.name}: ${error instanceof Error ? error.message : 'Upload failed'}`
                  );
                }
              }
            }

            logger.success(`Uploaded ${uploadedTraces} trace files`);
          } catch (error) {
            logger.warn(
              `Trace upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`
            );
          }
        } else if (traceDir) {
          // Fallback: Upload trace files from directory if no traces found in JSON
          try {
            if (this.config.verbose) {
              logger.verbose('Uploading traces from directory...');
            }

            // Upload traces with 'json/traces' prefix to match JSON attachment structure
            const traceUrls = await uploadService.uploadDirectoryWithProgress(
              traceDir,
              'json/traces'
            );

            logger.success(`Uploaded ${traceUrls.length} trace files`);
          } catch (error) {
            logger.warn(
              `Trace directory upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`
            );
          }
        } else {
          logger.warn('No trace files found');
        }
      }

      // Return result based on HTML upload status only
      if (this.config.uploadHtml) {
        if (htmlUrl) {
          return { status: 'uploaded', url: htmlUrl, urlMapping };
        } else {
          return { status: 'failed', url: '', urlMapping };
        }
      } else {
        return { status: 'disabled', url: '', urlMapping };
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      const emptyUrlMapping = new Map<string, string>();

      // If Azure upload fails completely and HTML upload was enabled, return failed
      if (this.config.uploadHtml) {
        logger.error(
          `TestDino platform upload failed completely: ${errorMessage}`
        );
        return { status: 'failed', url: '', urlMapping: emptyUrlMapping };
      } else {
        logger.warn(`TestDino platform upload failed: ${errorMessage}`);
        return { status: 'disabled', url: '', urlMapping: emptyUrlMapping };
      }
    }
  }

  /**
   * Build the final payload combining base report + metadata + Azure URLs
   * This must match the exact structure from sample-report.json
   */
  private buildFinalPayload(
    baseReport: PlaywrightReport,
    metadata: CompleteMetadata,
    azureUpload?: AzureUploadResult
  ): FinalPayload {
    // Attach Azure upload result to metadata if available
    if (azureUpload) {
      metadata.azureUpload = azureUpload;
    } else {
      // No Azure upload attempted - use default
      metadata.azureUpload = {
        status: 'not-found',
        url: '',
        urlMapping: new Map<string, string>(),
      };
    }

    // Build the payload EXACTLY matching sample-report.json structure
    const payload = {
      config: baseReport.config,
      suites: baseReport.suites,
      stats: baseReport.stats,
      errors: baseReport.errors ?? [],
      metadata,
    };

    return payload;
  }

  /**
   * Upload with graceful fallback for failed Azure uploads
   */
  async uploadWithFallback(
    jsonPath: string,
    htmlDir?: string,
    traceDir?: string
  ): Promise<ReportUploadResponse> {
    try {
      // Try full upload first
      return await this.uploadReport(jsonPath, htmlDir, traceDir);
    } catch (error) {
      if (error instanceof NetworkError && (htmlDir || traceDir)) {
        logger.warn('Full upload failed, attempting JSON-only upload...');

        // Fallback: try JSON-only upload
        try {
          return await this.uploadReport(jsonPath); // No HTML/traces
        } catch (fallbackError) {
          logger.error('Fallback upload also failed');
          throw fallbackError;
        }
      }

      throw error;
    }
  }
}
